// hooks/useSessionsData.js
import { useState, useEffect, useCallback, useMemo } from "react";
import moment from "moment";
import { sessionsAPI, trainersAPI, studentsAPI } from "../services/api";
import { groupSessionsForDisplay } from "../hooks/sessionGrouping";

export const useSessionsData = (
  organizationId,
  currentDate,
  calendarView,
  filters,
  customDateRange,
) => {
  const [allSessions, setAllSessions] = useState([]); // Ham datayı tutacak
  const [trainers, setTrainers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load trainers (cached to prevent redundant calls)
  const loadTrainers = useCallback(async () => {
    if (!organizationId) return;

    try {
      const response = await trainersAPI.getAll(organizationId, 2000);
      const trainersData = response.data.data || [];
      // Deduplicate trainers by ID to prevent React key warnings
      const uniqueTrainers = trainersData.filter(
        (trainer, index, self) =>
          index === self.findIndex((t) => t.id === trainer.id),
      );
      setTrainers(uniqueTrainers);
    } catch (error) {
      console.error("Error loading trainers:", error);
      setError("Failed to load trainers. Please try again.");
    }
  }, [organizationId]);

  // Load students (cached to prevent redundant calls)
  const loadStudents = useCallback(async () => {
    if (!organizationId) return;

    try {
      const response = await studentsAPI.getAll(organizationId, 2000);
      setStudents(response.data.data || []);
    } catch (error) {
      console.error("Error loading students:", error);
      setError("Failed to load students. Please try again.");
    }
  }, [organizationId]);

  // Get date range based on calendar view
  const getDateRange = useCallback((date, view) => {
    switch (view) {
      case "daily":
        return {
          startDate: moment(date).format("YYYY-MM-DD"),
          endDate: moment(date).format("YYYY-MM-DD"),
        };
      case "three_day":
        return {
          startDate: moment(date).format("YYYY-MM-DD"),
          endDate: moment(date).add(2, "days").format("YYYY-MM-DD"),
        };
      case "weekly":
        return {
          startDate: moment(date).startOf("week").format("YYYY-MM-DD"),
          endDate: moment(date).endOf("week").format("YYYY-MM-DD"),
        };
      case "monthly":
      default:
        return {
          startDate: moment(date).startOf("month").format("YYYY-MM-DD"),
          endDate: moment(date).endOf("month").format("YYYY-MM-DD"),
        };
    }
  }, []);

  // Get session color based on status
  const getSessionColor = (status) => {
    switch (status) {
      case "completed":
        return "green";
      case "cancelled":
        return "gray";
      case "no-show":
        return "red";
      default:
        return "blue";
    }
  };

  // Load sessions based on current parameters (WITHOUT filters)
  const loadSessions = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    try {
      let startDate, endDate;

      if (
        customDateRange &&
        customDateRange.startDate &&
        customDateRange.endDate
      ) {
        startDate = moment(customDateRange.startDate).format("YYYY-MM-DD");
        endDate = moment(customDateRange.endDate).format("YYYY-MM-DD");
      } else {
        const range = getDateRange(currentDate, calendarView);
        startDate = range.startDate;
        endDate = range.endDate;
      }

      let response;

      // Trainer seçimi varsa ona göre çek, yoksa tümünü çek
      if (filters.selectedTrainerId === "all") {
        response = await sessionsAPI.getByDateRange(
          organizationId,
          startDate,
          endDate,
        );
      } else {
        response = await sessionsAPI.getByTrainer(
          organizationId,
          filters.selectedTrainerId,
          startDate,
          endDate,
        );
      }

      const formattedSessions = (response.data.data || []).map((session) => ({
        ...session, // Spread all fields from the backend
        title: session.packageName, // Title will be enriched later
        start: new Date(session.startTime),
        end: new Date(session.endTime),
        sessionType: session.packageName,
        color: getSessionColor(session.status),
      }));

      // Ham datayı kaydet - FİLTRELEME YAPMA!
      setAllSessions(formattedSessions);
    } catch (error) {
      console.error("Error loading sessions:", error);
      setError("Failed to load sessions. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [
    organizationId,
    currentDate,
    calendarView,
    filters.selectedTrainerId,
    getDateRange,
    customDateRange,
  ]); // filters'ı kaldırdık, sadece selectedTrainerId kaldı
  const updateLocalSession = useCallback((sessionId, updateData) => {
    setAllSessions((prev) =>
      prev.map((session) =>
        session.id === sessionId || session.SK === sessionId
          ? { ...session, ...updateData, color: updateData.status ? getSessionColor(updateData.status) : session.color }
          : session,
      ),
    );
  }, []);

  const enrichedSessions = useMemo(() => {
    if (students.length === 0 || trainers.length === 0) {
      return allSessions;
    }

    return allSessions.map((session) => {
      const student = students.find((s) => s.id === session.studentId);
      const trainer = trainers.find((t) => t.id === session.trainerId);

      const studentName = student
        ? `${student.name} ${student.surname}`
        : "Unknown Student";
      const trainerName = trainer ? trainer.name : "Unknown Trainer";

      // Use packageName if available, fall back to sessionType or default
      const displayPackage =
        session.packageName || session.sessionType || "Session";

      return {
        ...session,
        studentName,
        trainerName,
        title: `${studentName} - ${displayPackage}`,
      };
    });
  }, [allSessions, students, trainers]);

  // Filtreleme sadece UI tarafında - useMemo ile optimize et
  const filteredSessions = useMemo(() => {
    let filtered = [...enrichedSessions];

    // Filter by selected trainers
    if (filters.selectedTrainers?.length > 0) {
      filtered = filtered.filter((session) =>
        filters.selectedTrainers.includes(session.trainerId),
      );
    }

    // Filter by student search
    if (filters.studentSearch) {
      filtered = filtered.filter((session) =>
        session.studentName
          .toLowerCase()
          .includes(filters.studentSearch.toLowerCase()),
      );
    }

    // Filter by session type
    if (filters.sessionType !== "all") {
      filtered = filtered.filter(
        (session) => session.sessionType === filters.sessionType,
      );
    }

    // Filter by session status
    if (filters.sessionStatus !== "all") {
      filtered = filtered.filter(
        (session) => session.status === filters.sessionStatus,
      );
    }

    // Group sessions by trainer, time, and package type
    return groupSessionsForDisplay(filtered);
  }, [
    enrichedSessions,
    filters.selectedTrainers,
    filters.studentSearch,
    filters.sessionType,
    filters.sessionStatus,
  ]);

  // Load initial data
  useEffect(() => {
    if (organizationId) {
      loadTrainers();
      loadStudents();
    }
  }, [organizationId, loadTrainers, loadStudents]);

  // Reload sessions when dependencies change (filters artık yok!)
  useEffect(() => {
    if (organizationId) {
      loadSessions();
    }
  }, [organizationId, loadSessions]);

  return {
    sessions: filteredSessions,
    allSessions: enrichedSessions,
    trainers,
    students,
    loading,
    error,
    loadSessions,
    loadTrainers,
    loadStudents,
    updateLocalSession,
  };
};
